/**
 * Feed Entity Schemas
 *
 * Types for activity feeds and reactions.
 */

import { z } from 'zod'
import { PollDefinitionSchema } from './poll'
import { FeedSyncedEmbedSchema } from './synced-embed'

/**
 * Activity Type Schema
 */
export const ActivityTypeSchema = z.enum([
  'POST',
  'ANNOUNCEMENT',
  'EVENT',
  'NEWS',
  'COMMENT',
  'LIKE',
  'SHARE',
  'SYSTEM'
])
export type ActivityType = z.infer<typeof ActivityTypeSchema>

/**
 * Feed Actor Schema (Enriched)
 * Backend enriches actors with additional user fields from database.
 */
export const FeedActorSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  photoUrl: z.string().nullable().optional(),
  // Enriched fields from UsersOnSchools
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  platformId: z.string().nullable().optional(),
  photoUrlThumbnail: z.string().nullable().optional(),
  photoUrlLarge: z.string().nullable().optional(),
  school: z
    .object({
      id: z.string(),
      name: z.string()
    })
    .optional()
})

export type FeedActor = z.infer<typeof FeedActorSchema>

/**
 * Cover Image Schema
 * Resolved cover for a post (feed + activity-detail share this shape).
 */
export const CoverImageSchema = z.object({
  id: z.string(),
  url: z.string(),
  thumbnailUrl: z.string().optional(),
  type: z.enum(['image', 'video', 'file'])
})

export type CoverImage = z.infer<typeof CoverImageSchema>

/**
 * The album an album post shares (SCH-2109, KTD4).
 *
 * The album's photos ride `attachments[]` as ordinary `image` items so a client
 * that predates this field still renders a normal photo post; this sibling adds
 * what those attachments cannot say: which album, its name, and the live
 * approved count behind the "+N" overlay. `available: false` means the album
 * was deleted and the post grants nothing (R13).
 */
export const FeedAlbumSchema = z.object({
  /** Collection public id. */
  id: z.string(),
  name: z.string(),
  /** Live count of approved media in the album. */
  mediaCount: z.number().int().nonnegative(),
  coverUrl: z.string().nullable(),
  available: z.boolean()
})

export type FeedAlbum = z.infer<typeof FeedAlbumSchema>

/**
 * Content Object Schema (Enriched)
 * Backend enriches content references with full ContentRecord data.
 */
export const ContentObjectSchema = z.object({
  id: z.string(),
  objectType: z.literal('content'),
  contentType: z.string(),
  contentTitle: z.string().nullable(),
  contentBody: z.string(),
  contentBodyFormat: z.enum(['PLAIN_TEXT', 'MARKDOWN', 'HTML']),
  reactionsEnabled: z.boolean(),
  commentsEnabled: z.boolean(),
  publisherType: z.string().optional(),
  school: z
    .object({
      id: z.string(),
      name: z.string(),
      /**
       * @deprecated `School.logoUrl` is populated on 0 of 103 production schools
       * and `logoThumbnailUrl` on 15 — a client that renders the school rung of
       * the attribution ladder from either ships blank for nearly every school.
       * Read `logoUrlLight` / `logoUrlDark` instead, picked by the viewer's
       * active color scheme. Kept on the wire so existing clients still parse.
       */
      logoUrl: z.string().nullable(),
      /** @deprecated See `logoUrl`. */
      logoThumbnailUrl: z.string().nullable(),
      // The live logo columns (46 of 103 schools populated, and the pair is set
      // together by the dashboard's branding form). Light and dark are separate
      // artwork, not tints — a school whose crest is dark ink supplies an
      // inverted file for dark mode — so the client picks by active scheme
      // rather than recoloring one image.
      logoUrlLight: z.string().nullable().optional(),
      logoUrlDark: z.string().nullable().optional()
    })
    .nullable()
    .optional(),
  author: z
    .object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string()
    })
    .nullable()
    .optional(),
  // The person credited as publisher on the ContentRecord. Shape mirrors the
  // activity-detail response so a client can render the same identity in the
  // list and on the post. Null when the content is published as the school.
  publisher: z
    .object({
      publicId: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      profileImageUrl: z.string().nullable().optional(),
      customImageUrl: z.string().nullable().optional(),
      platformId: z.string().nullable().optional()
    })
    .nullable()
    .optional(),
  /**
   * Free-text byline from the source system (Blackbaud `author`), e.g.
   * "IT Dept" or "King's Corner". Not a resolvable person, so neither
   * `author` nor `publisher` above can carry it — both require real name
   * fields. Clients render this when no publisher resolves.
   */
  byline: z.string().nullable().optional(),
  attachments: z
    .array(
      z.object({
        id: z.string().optional(),
        type: z.enum(['image', 'video', 'file']),
        /**
         * The locator a client fetches this attachment from.
         *
         * For a Mux-backed VIDEO this is the HLS manifest
         * (`https://stream.mux.com/<playbackId>.m3u8`) once the encode is
         * ready, and the `mux-pending://<mediaId>` sentinel while it is still
         * processing — never an S3 object URL, because a Mux row's `s3Key` is a
         * placeholder with no object behind it (see
         * `api/media/mux-urls.ts`). Nullable because a row can genuinely have
         * no locator at all; readers that render a bare `<img src>` must guard.
         */
        url: z.string().nullable(),
        thumbnailUrl: z.string().nullable().optional(),
        name: z.string().optional(),
        mediaPlatformId: z.string().optional(),
        /**
         * Stored MIME type and size, both of which the list read has always
         * sent. The MIME is the only thing that distinguishes an audio `file`
         * attachment from a document one — the wire type enum is closed and
         * both are `file` (SCH-2130, KTD2).
         */
        mimeType: z.string().nullable().optional(),
        fileSize: z.number().nullable().optional(),
        /**
         * Length in seconds, set only on a row that states one — in practice an
         * AUDIO attachment. `null` or absent means "unknown length", never
         * zero.
         */
        duration: z.number().nullable().optional(),
        /**
         * Mux public playback id, present only on Mux-backed VIDEO rows whose
         * encode has finished. Mirrors the field the media-library payload has
         * always exposed (`formatMedia`), so a client has ONE way to recognise
         * a Mux video regardless of which lane delivered it — instead of
         * pattern-matching `url`. Optional: absent on every non-video
         * attachment and on responses from a backend predating this field.
         */
        muxPlaybackId: z.string().nullable().optional()
      })
    )
    .nullable()
    .optional(),
  coverImage: CoverImageSchema.nullable().optional(),
  // Render-time cover URL resolved from the post's source rule (inbound email → matched rule).
  // For inbound-email posts: respects the rule's bannerMode (CUSTOM uses rule URL, AUTO uses
  // body-derived banner attachment, NONE returns null). For all other posts, falls back to
  // the existing coverImage URL. Always reflects the rule's current state — no backfill required.
  coverImageUrl: z.string().nullable().optional(),
  // SCH-2109: set only on an album post. Optional so old payloads still parse.
  album: FeedAlbumSchema.nullable().optional(),
  /**
   * SCH-2238: the poll's static definition, set only on a post that carries a
   * poll. No counts or viewer state (KTD5); those come from the poll endpoint.
   * Optional so old payloads still parse.
   */
  poll: PollDefinitionSchema.nullable().optional(),
  /**
   * True when the post is a Blackbaud news item or announcement copied in by
   * sync. Apps render the body's embeds (live frames, video cards, Open cards)
   * only for these posts, so composer and email posts render as before
   * (SCH-1104). Optional: absent on older payloads, which clients treat as
   * not synced.
   */
  isSyncedPost: z.boolean().optional(),
  /**
   * The sync-time classification of each embed in the body
   * (`ContentRecord.metadata.embeds`, rendering fields only, at most
   * `FEED_SYNCED_EMBEDS_MAX`). Sent only for synced posts that have entries.
   */
  embeds: z.array(FeedSyncedEmbedSchema).optional()
})

export type ContentObject = z.infer<typeof ContentObjectSchema>

/**
 * Feed Group Schema (Enriched)
 * Backend enriches with UserGroup data.
 */
export const FeedGroupSchema = z.object({
  id: z.string(),
  name: z.string(),
  avatarImageUrl: z.string().nullable().optional(),
  bannerImageUrl: z.string().nullable().optional(),
  bannerLogoUrl: z.string().nullable().optional(),
  isSystemGroup: z.boolean().optional(),
  commentsEnabled: z.boolean().optional(),
  reactionsEnabled: z.boolean().optional(),
  // Server-computed entry gate (plan 2026-06-25-001 U6 / R6 / KTD4):
  // `canOpen === effectiveVisible && viewerIsMember`. Clients render the
  // tappable space badge / allow navigation ONLY when this is `true`; a
  // missing/undefined value (latency, stale cache, or a pre-U6 backend) is
  // treated as `false` (no badge — R7). Deliberately a single boolean on the
  // EXISTING group block, never the raw `effectiveVisible` / `app_visibility`,
  // so a member never learns a hidden space's admin-set shown/hidden status
  // (KTD4 — no visibility leak). Optional so older clients/backends still parse.
  canOpen: z.boolean().optional()
})

export type FeedGroup = z.infer<typeof FeedGroupSchema>

/**
 * Feed Activity Schema (Enriched)
 * Backend enriches activities with actor, object, and group data from database.
 */
export const FeedActivitySchema = z.object({
  id: z.string(),
  // Kind is used by Stream for activity type identification
  kind: z.string().optional(),
  actor: FeedActorSchema,
  verb: z.string(),
  // Object can be either a string reference OR enriched ContentObject
  object: z.union([z.string(), ContentObjectSchema]).nullable().optional(),
  target: z.string().nullable().optional(),
  type: ActivityTypeSchema.optional(),
  title: z.string().nullable().optional(),
  body: z.string().nullable().optional(),
  imageUrl: z.string().nullable().optional(),
  foreignId: z.string().nullable().optional(),
  origin: z.string().nullable().optional(),
  time: z.string(),
  // Enriched group data
  group: FeedGroupSchema.optional(),
  // Reaction fields (camelCase)
  reactionCounts: z.record(z.string(), z.number()).optional(),
  ownReactions: z.record(z.string(), z.array(z.unknown())).optional(),
  latestReactions: z.record(z.string(), z.array(z.unknown())).optional(),
  // Reaction fields (snake_case alternatives from Stream)
  reaction_counts: z.record(z.string(), z.number()).optional(),
  own_reactions: z.record(z.string(), z.array(z.unknown())).optional(),
  latest_reactions: z.record(z.string(), z.array(z.unknown())).optional(),
  // Comments enabled flag
  commentsEnabled: z.boolean().optional(),
  extra: z.record(z.string(), z.unknown()).optional(),
  // Custom data payload (for activity creation)
  data: z.record(z.string(), z.unknown()).optional(),
  // For creating activities (before id/actor/time exist)
  userId: z.string().optional()
})

export type FeedActivity = z.infer<typeof FeedActivitySchema>

/**
 * Reaction User Data Schema (nested in user object)
 */
export const ReactionUserDataSchema = z.object({
  firstName: z.string().optional(),
  first_name: z.string().optional(),
  lastName: z.string().optional(),
  last_name: z.string().optional(),
  name: z.string().optional(),
  sj_school_connection_id: z.string().optional(),
  sj_platform_user_id: z.string().nullable().optional(),
  sj_school_name: z.string().optional()
})

/**
 * Reaction User Schema (for nested user info)
 */
export const ReactionUserSchema = z.object({
  id: z.string(),
  // Flat fields
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  name: z.string().optional(),
  photoUrl: z.string().nullable().optional(),
  // Nested data object (Stream format)
  data: ReactionUserDataSchema.optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional()
})

/**
 * Reaction Data Schema (for comment text, etc.)
 */
export const ReactionDataSchema = z
  .object({
    text: z.string().optional()
  })
  .passthrough()

/**
 * Reaction Child Schema (for nested likes/comments)
 * Note: Self-referencing requires a separate schema to avoid circular issues
 */
export const ReactionChildSchema = z.object({
  id: z.string(),
  kind: z.string().optional(),
  activityId: z.string().optional(),
  userId: z.string().optional(),
  data: ReactionDataSchema.optional(),
  user: ReactionUserSchema.optional(),
  createdAt: z.string().optional(),
  created_at: z.string().optional()
})

export type ReactionChild = z.infer<typeof ReactionChildSchema>

/**
 * Reaction Schema
 */
export const ReactionSchema = z.object({
  id: z.string(),
  kind: z.string(),
  activityId: z.string(),
  userId: z.string(),
  data: ReactionDataSchema.optional(),
  targetFeeds: z.array(z.string()).optional(),
  createdAt: z.string(),
  updatedAt: z.string().optional(),
  // Snake_case alternatives
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  // Enriched fields
  user: ReactionUserSchema.optional(),
  // Nested reactions (children) - typed as ReactionChild arrays
  latestChildren: z.record(z.string(), z.array(ReactionChildSchema)).optional(),
  ownChildren: z.record(z.string(), z.array(ReactionChildSchema)).optional(),
  childrenCounts: z.record(z.string(), z.number()).optional()
})

export type Reaction = z.infer<typeof ReactionSchema>

/**
 * Comment Schema
 */
export const CommentSchema = z.object({
  id: z.string(),
  activityId: z.string(),
  userId: z.string(),
  text: z.string(),
  user: FeedActorSchema.optional(),
  reactionCounts: z.record(z.string(), z.number()).optional(),
  createdAt: z.string(),
  updatedAt: z.string().optional()
})

export type Comment = z.infer<typeof CommentSchema>
export type ReactionData = z.infer<typeof ReactionDataSchema>
